import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import {
  useBillingSettings,
  useUpdateBillingSettings,
  type UpdateBillingSettingsInput,
} from "@/hooks/useBillingSettings";

const toNumber = (v: string, fallback: number) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : fallback;
};

export const BillingGlobalSettings = () => {
  const { data, isLoading } = useBillingSettings();
  const update = useUpdateBillingSettings();

  const [form, setForm] = useState({
    commissionRate: "",
    defaultTrialDays: "",
    gracePeriodDays: "",
    maxRetries: "",
    retryScheduleDays: "",
  });

  useEffect(() => {
    if (!data) return;
    setForm({
      commissionRate: String(data.commissionRate ?? 0),
      defaultTrialDays: String(data.defaultTrialDays ?? 14),
      gracePeriodDays: String(data.gracePeriodDays ?? 7),
      maxRetries: String(data.maxRetries ?? 3),
      retryScheduleDays: (data.retryScheduleDays ?? [1, 3, 7]).join(","),
    });
  }, [data]);

  const onSave = () => {
    const retrySchedule = form.retryScheduleDays
      .split(",")
      .map((s) => Number(s.trim()))
      .filter((n) => Number.isFinite(n) && n > 0);

    const payload: UpdateBillingSettingsInput = {
      commissionRate: toNumber(form.commissionRate, 0),
      defaultTrialDays: toNumber(form.defaultTrialDays, 14),
      gracePeriodDays: toNumber(form.gracePeriodDays, 7),
      maxRetries: toNumber(form.maxRetries, 3),
      retryScheduleDays: retrySchedule.length ? retrySchedule : [1, 3, 7],
    };

    update.mutate(payload, {
      onSuccess: () => toast.success("Billing settings saved"),
      onError: (e) => toast.error(e.message),
    });
  };

  if (isLoading) return <Skeleton className="h-44 w-full" />;

  return (
    <section className="rounded-card border border-border p-5 bg-card space-y-4">
      <div>
        <h2 className="text-lg font-semibold">Global billing settings</h2>
        <p className="text-sm text-muted-foreground">
          One commission rate applies to every plan. Existing subscriptions keep
          their recorded rate; only new and renewed subscriptions pick up
          changes here.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-3">
        <Field label="Commission (%)">
          <Input
            type="number"
            min={0}
            max={100}
            step={0.1}
            value={form.commissionRate}
            onChange={(e) =>
              setForm({ ...form, commissionRate: e.target.value })
            }
          />
        </Field>
        <Field label="Default trial (days)">
          <Input
            type="number"
            min={0}
            max={90}
            value={form.defaultTrialDays}
            onChange={(e) =>
              setForm({ ...form, defaultTrialDays: e.target.value })
            }
          />
        </Field>
        <Field label="Grace period (days)">
          <Input
            type="number"
            min={0}
            max={60}
            value={form.gracePeriodDays}
            onChange={(e) =>
              setForm({ ...form, gracePeriodDays: e.target.value })
            }
          />
        </Field>
        <Field label="Max retries">
          <Input
            type="number"
            min={0}
            max={10}
            value={form.maxRetries}
            onChange={(e) => setForm({ ...form, maxRetries: e.target.value })}
          />
        </Field>
        <Field label="Retry schedule (days)">
          <Input
            value={form.retryScheduleDays}
            placeholder="1,3,7"
            onChange={(e) =>
              setForm({ ...form, retryScheduleDays: e.target.value })
            }
          />
        </Field>
      </div>

      <div className="flex justify-end">
        <Button onClick={onSave} disabled={update.isPending}>
          {update.isPending ? "Saving…" : "Save settings"}
        </Button>
      </div>
    </section>
  );
};

const Field = ({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) => (
  <div className="space-y-1">
    <Label>{label}</Label>
    {children}
  </div>
);

export default BillingGlobalSettings;
